import { useCallback, useEffect } from 'react';

import { MimeType } from '@cofenster/constants';
import { useInterval, useRefOfState, useTimer } from '@cofenster/web-components';

export const useTrackWatchTime = (videoId: string, video: HTMLVideoElement | null) => {
  const trackUrl = `/api/a/watch/${videoId}`;
  const { time, start, stop, reset } = useTimer();
  const timeRef = useRefOfState(time);

  useEffect(() => {
    if (video) {
      video.addEventListener('playing', start);
      video.addEventListener('pause', stop);
      return () => {
        video.removeEventListener('playing', start);
        video.removeEventListener('pause', stop);
      };
    }
  }, [video, start, stop]);

  const sendAndFlush = useCallback(() => {
    if (timeRef.current < 1) return;
    if (
      navigator.sendBeacon(
        trackUrl,
        new Blob(
          [
            JSON.stringify({
              w: btoa(timeRef.current.toString(16)),
              t: btoa(Math.round((video?.currentTime ?? 0) * 1000).toString(16)),
            }),
          ],
          {
            type: MimeType.APPLICATION_JSON,
          }
        )
      )
    ) {
      reset();
    }
  }, [trackUrl, timeRef, reset, video]);

  useInterval(sendAndFlush, 3000);

  useOnVisibilityChange(
    useCallback(
      (visible: boolean) => {
        if (!visible) sendAndFlush();
      },
      [sendAndFlush]
    )
  );
};

const useOnVisibilityChange = (callback: (visible: boolean) => unknown) => {
  useEffect(() => {
    const listener = () => callback(window.document.visibilityState === 'visible');
    window.document.addEventListener('visibilitychange', listener);
    return () => window.document.removeEventListener('visibilitychange', listener);
  }, [callback]);
};
